import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import salesFlatRatesService from '@/services/salesFlatRates';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { buildUpdateSaleRequestData } from '@/composables/sales/useSalesCreateModal';
import { buildCreateSaleMultiCustomerRequestData } from '@/composables/salesMultiCustomer/useSalesMultiCustomerCreateModal';
import { createInitialSaleMetrics } from '@/composables/sales/saleDetail/state';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('sales');

export const saleDetailCoreMethods = {
  resolveRequestedTabKey() {
    const requested = String(this.$route?.query?.tab || '').trim().toLowerCase();
    if (requested === '') {
      return '';
    }

    if (requested === 'payment_plan') {
      return 'payment-plan';
    }

    return requested;
  },
  applyRequestedTabKey() {
    const requestedKey = this.resolveRequestedTabKey();
    if (requestedKey === '') {
      return;
    }

    const tabList = Array.isArray(this.tabs) ? this.tabs : [];
    const hasRequestedTab = tabList.some((tab) => String(tab?.key || '').trim() === requestedKey);
    if (!hasRequestedTab) {
      return;
    }

    this.activeTab = requestedKey;
  },
  openSalePaymentPlanModal(initialCustomerId = 0) {
    const customerId = Number(initialCustomerId || 0) || 0;
    this.paymentPlanInitialCustomerId = customerId > 0 ? customerId : 0;
    this.paymentPlanOpenModalRequestId += 1;
    this.activeTab = 'payment-plan';
  },
  isValidSaleDetailPayload(payload = {}) {
    if (!payload || typeof payload !== 'object') {
      return false;
    }

    const id = Number(payload?.id || payload?.sale_id || 0) || 0;
    return id > 0;
  },
  isMultiCustomerSalePayload(payload = {}) {
    const source = payload && typeof payload === 'object' ? payload : {};
    const countCandidates = [
      source?.customers_count,
      source?.companies_count,
      source?.clients_count,
    ];

    const hasManyCustomers = countCandidates.some((candidate) => Number(candidate || 0) > 1);
    const hasCustomersArray = Array.isArray(source?.customers) && source.customers.length > 1;
    const multiFlag = Number(source?.is_multi_customer || source?.multi_customer || 0) === 1;
    const typeLabel = String(source?.sale_type || source?.type || source?.sale_mode || '').trim().toLowerCase();

    return hasManyCustomers || hasCustomersArray || multiFlag || typeLabel.includes('multi');
  },
  isFlatRateSalePayload(payload = {}) {
    const source = payload && typeof payload === 'object' ? payload : {};

    const modeLabel = String(source?.sale_mode || source?.sale_type || source?.type || '').trim().toLowerCase();
    if (modeLabel.includes('iguala') || modeLabel.includes('retainer')) {
      return true;
    }

    const serviceCode = String(source?.service_type_code || '').trim().toUpperCase();
    if (serviceCode === 'IGL') {
      return true;
    }

    const serviceTypeLabel = String(source?.service_type_name || source?.service_type || '').trim().toLowerCase();
    return serviceTypeLabel.includes('iguala') || serviceTypeLabel.includes('retainer');
  },
  isValidSaleMetricsPayload(payload = {}) {
    if (!payload || typeof payload !== 'object') {
      return false;
    }

    const hasNumericMetric = [
      payload?.income,
      payload?.expenses,
      payload?.commissions,
      payload?.utility,
      payload?.margin,
      payload?.total_income,
      payload?.total_expenses,
      payload?.total_commissions,
      payload?.profit,
      payload?.profit_margin,
    ].some((value) => Number.isFinite(Number(value)));

    return hasNumericMetric;
  },
  resolveMetricsValue(payload = {}, keys = []) {
    const keyList = Array.isArray(keys) ? keys : [];
    const match = keyList.find((key) => Number.isFinite(Number(payload?.[key])));
    return match ? Number(payload?.[match] || 0) : 0;
  },
  normalizeMetricsSummarySection(section = null, fallbackTotal = 0) {
    const source = section && typeof section === 'object' ? section : {};
    const toNumber = (value, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const total = toNumber(source.total, toNumber(fallbackTotal, 0));

    return {
      subtotal: toNumber(source.subtotal, total),
      transferred_taxes: toNumber(source.transferred_taxes, toNumber(source.transfered_taxes, 0)),
      withheld_taxes: toNumber(source.withheld_taxes, 0),
      total,
    };
  },
  resolveMetricsSummarySection(payload = {}, key) {
    const summary = payload && typeof payload.summary === 'object' ? payload.summary : {};
    const fallbackByKey = {
      income: this.resolveMetricsValue(payload, ['income', 'total_income', 'incomes', 'revenue']),
      expenses: this.resolveMetricsValue(payload, ['expenses', 'total_expenses', 'costs']),
      commissions: this.resolveMetricsValue(payload, ['commissions', 'total_commissions', 'fees']),
    };

    return this.normalizeMetricsSummarySection(
      summary?.[key] || payload?.[`${key}_summary`] || payload?.[`${key}Summary`],
      fallbackByKey[key] || 0
    );
  },
  normalizeSaleMetricsPayload(payload = {}) {
    const source = payload && typeof payload === 'object' ? payload : {};

    const incomeSummary = this.resolveMetricsSummarySection(source, 'income');
    const expensesSummary = this.resolveMetricsSummarySection(source, 'expenses');
    const commissionsSummary = this.resolveMetricsSummarySection(source, 'commissions');

    const income = Number(incomeSummary.total || 0) || 0;
    const expenses = Number(expensesSummary.total || 0) || 0;
    const commissions = Number(commissionsSummary.total || 0) || 0;
    const incomeSubtotal = Number(incomeSummary.subtotal || 0) || 0;
    const expensesSubtotal = Number(expensesSummary.subtotal || 0) || 0;
    const commissionsSubtotal = Number(commissionsSummary.subtotal || 0) || 0;
    const utility = this.resolveMetricsValue(source, ['utility', 'profit']);
    const margin = this.resolveMetricsValue(source, ['margin', 'profit_margin', 'utility_margin']);
    const utilitySubtotal = this.resolveMetricsValue(source, ['utility_subtotal', 'utilitySubtotal']);
    const marginSubtotal = this.resolveMetricsValue(source, ['margin_subtotal', 'marginSubtotal']);

    const computedUtility = utility !== 0 ? utility : (income - expenses - commissions);
    const computedUtilitySubtotal = utilitySubtotal !== 0
      ? utilitySubtotal
      : (incomeSubtotal - expensesSubtotal - commissionsSubtotal);
    const computedMarginSubtotal = marginSubtotal !== 0
      ? marginSubtotal
      : (incomeSubtotal > 0 ? ((computedUtilitySubtotal / incomeSubtotal) * 100) : 0);

    return {
      income,
      expenses,
      commissions,
      utility: computedUtility,
      margin,
      utility_subtotal: computedUtilitySubtotal,
      margin_subtotal: computedMarginSubtotal,
      utilitySubtotal: computedUtilitySubtotal,
      marginSubtotal: computedMarginSubtotal,
      summary: {
        income: incomeSummary,
        expenses: expensesSummary,
        commissions: commissionsSummary,
      },
      income_summary: incomeSummary,
      expenses_summary: expensesSummary,
      commissions_summary: commissionsSummary,
    };
  },
  normalizeMultiCustomerSalePayload(rawSale = {}) {
    const source = rawSale && typeof rawSale === 'object' ? rawSale : {};

    return {
      ...source,
      company_name: String(source?.company_name || '').trim() || text.detail.labels.multiCustomer,
      sale_mode: String(source?.sale_mode || 'multi-customer').trim() || 'multi-customer',
      sale_type: String(source?.sale_type || 'multi-customer').trim() || 'multi-customer',
      is_multi_customer: Number(source?.is_multi_customer || source?.multi_customer || 1) === 1 ? 1 : 1,
      customers_count: Math.max(
        2,
        Number(source?.customers_count || source?.companies_count || source?.clients_count || 0) || 0
      ),
      subtotal: Number(source?.subtotal || source?.sale_price || 0) || 0,
      sale_price: Number(source?.sale_price || source?.subtotal || 0) || 0,
      budget_general: Number(source?.budget_general || source?.general_budget || 0) || 0,
      media_amount: Number(source?.media_amount || source?.advertising_amount || 0) || 0,
      agency_amount: Number(source?.agency_amount || source?.sale_import || 0) || 0,
      advertising_amount: Number(source?.advertising_amount || source?.media_amount || 0) || 0,
      sale_import: Number(source?.sale_import || source?.agency_amount || 0) || 0,
    };
  },
  async resolveSaleDetailPayload() {
    try {
      const response = await salesService.show(this.saleId, {}, { skipLoading: true });
      const payload = extractPayload(response);

      if (this.isValidSaleDetailPayload(payload)) {
        return payload;
      }
    } catch (error) {
      // Fallback to multicliente endpoint below.
    }

    const multiResponse = await salesMultiCustomerService.index(
      { sale_id: this.saleId },
      { skipLoading: true }
    );

    const rows = extractCollection(multiResponse);
    const matched = rows.find((row) => Number(row?.id || 0) === Number(this.saleId));

    if (matched && typeof matched === 'object') {
      return this.normalizeMultiCustomerSalePayload(matched);
    }

    throw new Error('SALE_NOT_FOUND');
  },
  async resolveSaleMetricsPayload() {
    try {
      const response = await salesService.detailMetrics(this.saleId, {}, { skipLoading: true });
      const payload = extractPayload(response);
      if (this.isValidSaleMetricsPayload(payload)) {
        return payload;
      }
    } catch (error) {
      // Keep default metrics when endpoint fails.
    }

    return {};
  },
  ensureActiveTabIsValid() {
    const tabList = Array.isArray(this.tabs) ? this.tabs : [];
    const active = String(this.activeTab || '').trim();
    if (tabList.some((tab) => String(tab?.key || '') === active)) {
      return;
    }

    this.activeTab = tabList[0]?.key || 'summary';
  },
  async fetchSaleDetail() {
    if (!this.saleId) {
      this.$toast?.error(text.detail.toasts.invalidSale);
      this.$router.push({ name: 'sales' });
      return;
    }

    this.loading = true;

    try {
      const salePayload = await this.resolveSaleDetailPayload();
      const metricsPayload = await this.resolveSaleMetricsPayload();

      this.sale = salePayload && typeof salePayload === 'object'
        ? salePayload
        : null;

      this.saleMetrics = this.normalizeSaleMetricsPayload(metricsPayload);

      this.ensureActiveTabIsValid();
      this.applyRequestedTabKey();

      if (this.activeTab === 'expenses') {
        await this.fetchSaleExpenses();
      } else if (this.activeTab === 'commissions') {
        await this.fetchSaleFees();
      }
    } catch (error) {
      this.sale = null;
      this.saleMetrics = createInitialSaleMetrics();
      this.$toast?.error(error?.response?.data?.message || text.detail.toasts.loadDetailError);
    } finally {
      this.loading = false;
    }
  },
  handleSaleNewPaymentPlan() {
    this.openSalePaymentPlanModal();
  },
  handleSaleInvoicePlan(row) {
    const paymentId = Number(row?.id || 0) || 0;
    if (paymentId <= 0) {
      this.$toast?.error(text.detail.toasts.invoicePlanMissingId);
      return;
    }

    this.$router.push({
      name: 'invoiceCreate',
      query: {
        payment_id: String(paymentId),
      },
    });
  },
  async handleSalePaymentPlanSaved() {
    await this.fetchSaleDetail();
  },
  cleanupSaleDetail() {
    this.clearExpenseSearchDebounce();
    this.clearFeeSearchDebounce();
  },
  onHeaderAction(action) {
    const key = String(action?.key || '').trim();

    if (key === 'edit-sale') {
      this.openSaleEditModal('general');
      return;
    }

    if (key === 'sale-sheet') {
      this.activeTab = 'summary';
      return;
    }

    if (key === 'add-calendar') {
      this.$toast?.info(text.detail.toasts.addCalendarPending);
      return;
    }

    if (key === 'new-payment-plan') {
      this.openSalePaymentPlanModal();
      return;
    }

    if (key === 'new-expense') {
      this.openExpenseModal();
      return;
    }

    if (key === 'new-fee') {
      this.openFeeModal();
    }
  },
  openSaleEditModal(sectionKey = 'general') {
    this.saleEditSectionKey = String(sectionKey || 'general').trim().toLowerCase() || 'general';
    this.isSaleEditModalOpen = true;
  },
  closeSaleEditModal(force = false) {
    if (!force && this.saleEditSubmitting) {
      return;
    }

    this.isSaleEditModalOpen = false;
    this.saleEditSectionKey = 'general';

    if (force) {
      this.closeDeleteSaleConfirmModal();
    }
  },
  handleEditDetailSection(sectionKey = 'details') {
    this.openSaleEditModal(sectionKey);
  },
  async submitSaleEdit(payload) {
    if (!this.saleId) {
      return;
    }

    this.saleEditSubmitting = true;

    try {
      const requestData = buildUpdateSaleRequestData(payload);
      const useFlatRateUpdate = this.isFlatRateSalePayload(this.sale);
      const response = useFlatRateUpdate
        ? await salesFlatRatesService.update(this.saleId, requestData)
        : await salesService.update(this.saleId, requestData);

      const fallbackErrorMessage = useFlatRateUpdate
        ? text.detail.toasts.updateFlatRateError
        : text.detail.toasts.updateBusinessError;
      const fallbackSuccessMessage = useFlatRateUpdate
        ? text.detail.toasts.updateFlatRateSuccess
        : text.detail.toasts.updateBusinessSuccess;

      const apiErrorMessage = extractApiErrorMessage(response, fallbackErrorMessage);
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, fallbackSuccessMessage));
      this.closeSaleEditModal(true);
      await this.fetchSaleDetail();
    } catch (error) {
      this.$toast?.error(
        error?.response?.data?.message
          || (this.isFlatRateSalePayload(this.sale)
            ? text.detail.toasts.updateFlatRateError
            : text.detail.toasts.updateBusinessError)
      );
    } finally {
      this.saleEditSubmitting = false;
    }
  },
  async submitSaleMultiCustomerEdit(payload) {
    if (!this.saleId) {
      return;
    }

    this.saleEditSubmitting = true;

    try {
      const requestData = buildCreateSaleMultiCustomerRequestData(payload);
      const response = await salesMultiCustomerService.update(this.saleId, requestData);

      const apiErrorMessage = extractApiErrorMessage(response, text.detail.toasts.updateMultiCustomerError);
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, text.detail.toasts.updateMultiCustomerSuccess));
      this.closeSaleEditModal(true);
      await this.fetchSaleDetail();
    } catch (error) {
      this.$toast?.error(error?.response?.data?.message || text.detail.toasts.updateMultiCustomerError);
    } finally {
      this.saleEditSubmitting = false;
    }
  },
  handleDeleteSaleFromModal(sale) {
    const saleId = Number(sale?.id || this.saleId || 0);
    if (saleId <= 0) {
      return;
    }

    this.deletingSaleFromDetail = {
      ...(sale && typeof sale === 'object' ? sale : {}),
      id: saleId,
      sale_name: String(
        sale?.sale_name
        || sale?.name
        || this.sale?.sale_name
        || this.sale?.name
        || ''
      ).trim(),
    };

    this.isDeleteSaleConfirmModalOpen = true;
  },
  closeDeleteSaleConfirmModal() {
    this.isDeleteSaleConfirmModalOpen = false;
    this.deletingSaleFromDetail = null;
  },
  resolveSaleListRouteName() {
    if (this.isMultiCustomerSale) {
      return 'salesMultiCustomer';
    }

    if (this.isFlatRateSale) {
      return 'salesFlatRates';
    }

    return 'sales';
  },
  async confirmDeleteSaleFromDetail() {
    const saleId = Number(this.deletingSaleFromDetail?.id || this.saleId || 0);
    if (saleId <= 0) {
      this.closeDeleteSaleConfirmModal();
      return;
    }

    const isMultiCustomer = this.isMultiCustomerSalePayload(this.sale);
    const isFlatRate = !isMultiCustomer && this.isFlatRateSalePayload(this.sale);

    const deleteService = isMultiCustomer
      ? salesMultiCustomerService
      : (isFlatRate ? salesFlatRatesService : salesService);

    const deleteErrorMessage = isMultiCustomer
      ? text.detail.toasts.deleteMultiCustomerError
      : (isFlatRate ? text.detail.toasts.deleteFlatRateError : text.detail.toasts.deleteBusinessError);

    const deleteSuccessMessage = isMultiCustomer
      ? text.detail.toasts.deleteMultiCustomerSuccess
      : (isFlatRate ? text.detail.toasts.deleteFlatRateSuccess : text.detail.toasts.deleteBusinessSuccess);

    try {
      const response = await deleteService.delete(saleId);
      const apiErrorMessage = extractApiErrorMessage(response, deleteErrorMessage);
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, deleteSuccessMessage));
      this.closeSaleEditModal(true);
      this.closeDeleteSaleConfirmModal();
      this.$router.push({ name: this.resolveSaleListRouteName() });
    } catch (error) {
      this.$toast?.error(error?.response?.data?.message || deleteErrorMessage);
    }
  },
};
